import React, { useEffect, useMemo, useState } from "react";
import { Check, Search } from "lucide-react";
import { apiGet } from "../../processes/helpers/api";
import { studentSearchQuery } from "./teacherMocksHelpers";

export default function PublicationSpecificStudentsPanel({ values, isSubmitting, onAddStudent, onUpdateStudents }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const selectedSet = useMemo(() => new Set(values.studentIds), [values.studentIds]);
  const totalPages = pagination?.totalPages ?? 1;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!values.courseId) {
      setResults([]);
      setPagination(null);
      return undefined;
    }

    let isMounted = true;

    async function loadStudents() {
      try {
        setIsLoading(true);
        const query = studentSearchQuery({ search: debouncedSearch, page, courseId: values.courseId });
        const response = await apiGet(`/api/v2/manager/users?${query}`);
        if (!isMounted) return;
        setResults(response.body?.data || []);
        setPagination(response.body?.pagination || null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadStudents();

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch, page, values.courseId]);

  const toggleStudent = (student) => {
    if (selectedSet.has(student.id)) {
      const nextIds = values.studentIds.filter((id) => Number(id) !== Number(student.id));
      const nextNames = { ...values.studentNames };
      delete nextNames[student.id];
      onUpdateStudents(nextIds, nextNames);
      return;
    }

    onAddStudent(student);
  };

  return (
    <div className="teacher-mocks-publication-students-panel">
      <div className="teacher-mocks-publication-students-search">
        <Search size={13} aria-hidden="true" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar aluno..."
          disabled={isSubmitting || !values.courseId}
        />
      </div>

      <div className="teacher-mocks-publication-students-list">
        {isLoading && <div className="teacher-mocks-publication-students-state">Buscando...</div>}
        {!isLoading && results.length === 0 && (
          <div className="teacher-mocks-publication-students-state">
            {values.courseId ? "Nenhum aluno encontrado." : "Selecione um curso para buscar alunos."}
          </div>
        )}
        {!isLoading && results.map((student) => {
          const selected = selectedSet.has(student.id);
          return (
            <button
              key={student.id}
              type="button"
              className={`teacher-mocks-publication-student-row${selected ? " selected" : ""}`}
              onClick={() => toggleStudent(student)}
              disabled={isSubmitting}
            >
              <span className="teacher-mocks-publication-student-check">
                {selected && <Check size={12} aria-hidden="true" />}
              </span>
              <span>
                <strong>{student.name}</strong>
                <small>{student.email}</small>
              </span>
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="teacher-mocks-publication-students-pagination">
          <button type="button" disabled={page <= 1 || isSubmitting} onClick={() => setPage((current) => current - 1)}>
            Anterior
          </button>
          <span>{page} / {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages || isSubmitting}
            onClick={() => setPage((current) => current + 1)}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}
